import React from "react";
import {
	CopilotContainer,
	TopBar,
	AIBadge,
	RefreshButton,
	HintText,
	EmptyGrid,
	EmptyLeftColumn,
	EmptyRightColumn,
	EmptyTextContainer,
	EmptyTitle,
	EmptySub,
	StartFirstEvalButton,
	InitialState,
	InitialSkeleton,
} from "./styles";
import {
	CopilotSummarySnapshot,
	useAICopilotViewModel,
} from "./useAICopilotViewModel";
import CopilotHeader from "./components/CopilotHeader";
import CopilotSummary from "./components/CopilotSummary";
import CopilotActions from "./components/CopilotActions";
import { ReactComponent as StarIcon } from "../../../../assets/kinologyIcons/svg/Star4.svg";
import { ReactComponent as RefreshIcon } from "../../../../assets/kinologyIcons/svg/cached.svg";
import { ReactComponent as PlayIcon } from "../../../../assets/kinologyIcons/svg/play_circle_white.svg";
import { CopilotPatientReference } from "../../services/clinicalDashboardService";

interface AICopilotSectionProps {
	patient: CopilotPatientReference | null;
	onSelectPatientClick?: () => void;
	onOpenRecord: () => void;
	initialLoading?: boolean;
	initialError?: string | null;
	onRetryInitial?: () => void;
	onSummaryStateChange?: (snapshot: CopilotSummarySnapshot) => void;
	onRefreshLatestPatient?: () => CopilotPatientReference | null;
}

const AICopilotSection: React.FC<AICopilotSectionProps> = ({
	patient: externalPatient,
	onSelectPatientClick,
	onOpenRecord,
	initialLoading = false,
	initialError,
	onRetryInitial,
	onSummaryStateChange,
	onRefreshLatestPatient,
}) => {
	const {
		activeTab,
		patient,
		summaryText,
		summaryTitle,
		handleSendReport,
		handleViewSummary,
		handleViewEvolution,
		handleViewAttentionPoints,
		handleRefreshSummary,
		handleStartFirstEvaluation,
		contentLoading,
		contentError,
		contentEmpty,
		sendingReport,
		reportError,
		handleRetryContent,
	} = useAICopilotViewModel(
		externalPatient,
		onOpenRecord,
		onSummaryStateChange,
		onRefreshLatestPatient
	);

	const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
		const target = event.target as HTMLElement;
		if (target.closest("button, a, input, textarea, select, [role='button']")) {
			return;
		}
		onOpenRecord();
	};

	return (
		<CopilotContainer
			isV1Mode={false}
			isEmpty={!patient}
			aria-labelledby="copilot-section-title"
			onClick={patient ? handleCardClick : undefined}
		>
			{initialLoading && !patient ? (
				<InitialState
					role="status"
					aria-label="Carregando conteúdo"
					aria-busy="true"
				>
					<InitialSkeleton />
					<InitialSkeleton />
					<InitialSkeleton className="short" />
				</InitialState>
			) : initialError && !patient ? (
				<InitialState role="alert">
					<span>{initialError}</span>
					{onRetryInitial && (
						<button type="button" onClick={onRetryInitial}>
							Tentar novamente
						</button>
					)}
				</InitialState>
			) : !patient ? (
				<EmptyGrid>
					<EmptyLeftColumn>
						<AIBadge id="copilot-section-title">
							<StarIcon width="10px" height="10px" />
							Copiloto Clínico IA
						</AIBadge>
						<EmptyTextContainer>
							<EmptyTitle>Bem-vindo à Kinology</EmptyTitle>
							<EmptySub>Inicie a sua primeira avaliação</EmptySub>
						</EmptyTextContainer>
					</EmptyLeftColumn>
					<EmptyRightColumn>
						<StartFirstEvalButton
							type="button"
							onClick={handleStartFirstEvaluation}
							aria-label="Iniciar Primeira Avaliação"
						>
							<PlayIcon width={15} height={15} />
							Iniciar Primeira Avaliação
						</StartFirstEvalButton>
					</EmptyRightColumn>
				</EmptyGrid>
			) : (
				<>
					<TopBar isEmpty={false}>
						<AIBadge id="copilot-section-title">
							<StarIcon width="10px" height="10px" />
							Copiloto Clínico IA
						</AIBadge>
						<RefreshButton
							onClick={handleRefreshSummary}
							title="Atualizar conteúdo"
							aria-label="Atualizar conteúdo"
							disabled={contentLoading}
						>
							<RefreshIcon width="16px" height="16px" fill="#b8f1e2" />
						</RefreshButton>
					</TopBar>

					<CopilotHeader
						patient={patient}
						isV1Mode={false}
						onClick={onSelectPatientClick}
					/>

					<CopilotSummary
						summaryText={summaryText}
						isV1Mode={false}
						activeTab={activeTab}
						summaryTitle={summaryTitle}
						isLoading={contentLoading}
						error={contentError}
						isEmpty={contentEmpty}
						onRetry={handleRetryContent}
					/>

					<CopilotActions
						isV1Mode={false}
						activeTab={activeTab}
						onSendReport={handleSendReport}
						onViewSummary={handleViewSummary}
						onViewEvolution={handleViewEvolution}
						onViewAttentionPoints={handleViewAttentionPoints}
						sendingReport={sendingReport}
					/>

					{reportError && <HintText role="alert">{reportError}</HintText>}
					<HintText>Clique no card para abrir o prontuário completo</HintText>
				</>
			)}
		</CopilotContainer>
	);
};

export default AICopilotSection;
